import {EnvironmentInjector, Injectable, runInInjectionContext} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as SharedActions from '../store-shared/shared.actions';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {
  CollectionSharedSuppliersService,
} from '../../../shared/shared-services/firebase/collection-shared-suppliers.service';
import {
  CollectionSharedDebtorsService,
} from '../../../shared/shared-services/firebase/collection-shared-debtors.service';
import {EMPTY, from, of, Subject} from 'rxjs';
import {
  CollectionUserAccessColleaguesService,
} from '../../../shared/shared-services/firebase/collection-user-access-colleagues.service';
import {StorageService} from '../../../shared/shared-services/firebase/storage.service';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';
import {
  CollectionOperationalMessagesServiceService,
} from '../../../shared/shared-services/firebase/collection-operational-messages.service';
import {IMessage, IMessageAutoOrderUpdate, TimeEnum} from '../models';
import {path_operational_stores_data_storeId_messages_from_app} from '../../../shared/shared-services/database-paths';
import {
  CollectionUsersMessagesService,
} from '../../../shared/shared-services/firebase/collection-users-messages.service';
import {selectSuppliersByUserSelectedStore} from './shared.selectors';
import {
  CollectionSharedSingularDocumentsService,
} from '../../../shared/shared-services/firebase/collection-shared-singular-documents.service';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {ISharedStoreSettings} from '../../../shared/shared-models/firebase/shared-store-settings';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import * as AutoOrderActions from '../../auto-ordering/store/auto-ordering.actions';
import * as AutoOrderSelectors from '../../auto-ordering/store/auto-ordering.selectors';
import {TypeSenseService} from '../../../shared/shared-services/type-sense/type-sense.service';
import {IFacetCounts, TypeSenseSearchResponse} from '../../../shared/shared-models/type-sense/type-sense-types';
import {
  createFacetCountsMap,
  mapTypeSenseObjectToGeneric,
} from '../../../shared/shared-utils/typesense-utils/typesense.utils';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';

import {IColleagues} from '../../user-access/models';
import * as UserAccessSelectors from '../../user-access/user-store/user.selectors';
import {selectIsUserLoggedIn, selectUser} from '../../user-access/user-store/user.selectors';
import {SharedToastService} from '../../../shared/shared-services/overlay/shared-toast.service';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import {selectSelectedStore, selectStores} from '../../user-access/user-stores-store/user-store.selectors';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {doc, docData, Firestore, getDoc} from '@angular/fire/firestore';
import {TLineColour} from '../../settings-new/models';
import {startHardRefreshWithFollowUp} from '../../../shared/shared-utils/browser/hard-refresh.utils';
import {BUILD_VERSION} from '../../../../environments/build-version';
import {environment} from '../../../../environments/environment';
import {createGlobalPaths} from '../../../shared/shared-services/database-paths.global';

const APP_LOAD_REFRESH_VERSION_KEY = 'appLoadRefreshVersion';
const VERSION_DOCUMENT_PATH = createGlobalPaths().version(environment.versionDocumentId);

/**
 * Reads the version this tab has already hard refreshed for. Storage can be unavailable
 * (private mode, blocked site data), in which case the refresh still runs and the guard
 * simply does not hold across the reload.
 *
 * @returns {string | null} The stored version, or `null` when unavailable.
 */
function getAppLoadRefreshedVersion(): string | null {
  try {
    return sessionStorage.getItem(APP_LOAD_REFRESH_VERSION_KEY);
  } catch {
    return null;
  }
}

/**
 * Records the version this tab is about to hard refresh for.
 *
 * @param {string} version - Version published by the server.
 */
function setAppLoadRefreshedVersion(version: string): void {
  try {
    sessionStorage.setItem(APP_LOAD_REFRESH_VERSION_KEY, version);
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

@Injectable()
export class SharedEffects {

  // '[Core Shared][Suppliers] Get Store Suppliers By User Selected Store'
  getStoreSuppliersByUserSelectedStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getStoreSuppliersByUserSelectedStore),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectSuppliersByUserSelectedStore)),
        this.store.pipe(select(selectStores)),
      ),
      map(([{dispatch}, selectedUserStores, suppliers, userStores]) => {
        if (Object.keys(suppliers).length > 0) {
          if (dispatch) {
            return SharedActions.getStoreSuppliersByStore({store: selectedUserStores});
          } else {
            if (selectedUserStores?.[0]) {
              return SharedActions.getStoreSuppliersByStoreSuccess({
                store: selectedUserStores[0],
                suppliers,
              });
            } else {
              return SharedActions.getStoreSuppliersByStoreSuccess({
                store: userStores[0],
                suppliers,
              });
            }
          }
        } else {
          return SharedActions.getStoreSuppliersByStore({store: selectedUserStores});
        }
      }),
    ),
  );

  // '[Core Shared][Suppliers] Get Store Suppliers By Store',
  getStoreSuppliersByStore$ = createEffect(() => this.actions$.pipe(
    ofType(SharedActions.getStoreSuppliersByStore),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(UserAccessSelectors.selectUser)),
    ),
    mergeMap(([action, store, user]) =>
      this.collectionSharedSuppliersService.getSuppliersByStore(action.store)
        .pipe(
          map((suppliers: Record<string, ISupplier>) =>
            SharedActions.getStoreSuppliersByStoreSuccess({
              suppliers,
              store: action.store,
            })),
          catchError((error) => of(
            SharedActions.getStoreSuppliersByStoreFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
    ),
  ));

  // '[Core Shared][Debtors] Get Store Debtors By User Selected Store'
  getStoreDebtorsByUserSelectedStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getStoreDebtorsByUserSelectedStore),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectStores)),
      ),
      filter(([, selectedStore]) => !!selectedStore?.storeId),
      map(([, selectedStore]) => SharedActions.getStoreDebtorsByStore({store: selectedStore})),
    ),
  );

  // '[Core Shared][Debtors] Get Store Debtors By Store'
  getStoreDebtorsByStore$ = createEffect(() => this.actions$.pipe(
    ofType(SharedActions.getStoreDebtorsByStore),
    filter((action) => !!action.store?.storeId),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(UserAccessSelectors.selectUser)),
    ),
    mergeMap(([action, store, user]) =>
      this.collectionSharedDebtorsService.getDebtorsByStore(action.store)
        .pipe(
          map((debtors) =>
            SharedActions.getStoreDebtorsByStoreSuccess({
              debtors,
              store: action.store,
            })),
          catchError((error) => of(
            SharedActions.getStoreDebtorsByStoreFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
    ),
  ));

  fetchStoreDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchStoreDepartments),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        this.firebaseService.getDepartmentsByStore(store).pipe(
          switchMap((departments) =>
            from(
              this.collectionSharedSingularDocumentsService.getDocument(
                createSharedPaths().setStoreId(store.storeId).storeSettings(),
              ),
            ).pipe(
              map((storeSettings) =>
                SharedActions.fetchStoreDepartmentsSuccess({
                  storeId: store.storeId,
                  departments,
                  storeSettings: (storeSettings as ISharedStoreSettings),
                }),
              ),
              catchError((error) =>
                of(SharedActions.fetchStoreDepartmentsFailure({
                  storeId: store.storeId,
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store?.storeId ?? 'unknown'],
                    userId: user?.id ?? 'unknown',
                  },
                })),
              ),
            ),
          ),
          catchError((error) =>
            of(SharedActions.fetchStoreDepartmentsFailure({
              storeId: store.storeId,
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
      ),
    ),
  );

  getLineColour$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getLineColour),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(this.firebaseService.getStoreDataDoc('line_colours', store.storeId)).pipe(
          map((colour: TLineColour) =>
            SharedActions.getLineColourSuccess({lineColour: colour, store}),
          ),
          catchError((error) =>
            of(SharedActions.getLineColourFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
              store,
            })),
          ),
        ),
      ),
    ),
  );

  // [Core Shared][Storage] Get Download URL from Storage
  getDownloadUrlFromStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getDownloadUrlFromStorage),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(this.storageService.getDocumentDownloadUrl(action.storage, store))
          .pipe(
            map((documentUrl: string) => SharedActions.getDownloadUrlFromStorageSuccess({
              storage: {
                ...action.storage,
                url: documentUrl,
              },
            })),
            catchError((error) => {
                return of(SharedActions.getDownloadUrlFromStorageFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store?.storeId ?? 'unknown'],
                    userId: user?.id ?? 'unknown',
                  },
                }));
              },
            ),
          ),
      ),
    ),
  );

  //  '[Core Shared][Messages] Get Messages From App for Auto Order Updates',
  getMessagesFromAppForAutoOrderUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getMessagesFromAppForAutoOrderUpdates),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(this.collectionOperationalMessagesServiceService.getMessagesUsingQuery<IMessageAutoOrderUpdate[]>(
          path_operational_stores_data_storeId_messages_from_app(store.storeId),
          action.queries,
        ))
          .pipe(
            map((messages: IMessage<IMessageAutoOrderUpdate[]>[]) => SharedActions
              .getMessagesFromAppForAutoOrderUpdatesSuccess({
                queries: action.queries,
                messageType: action.messageType,
                messages,
                store,
              })),
            catchError((error) =>
              of(SharedActions.getMessagesFromAppForAutoOrderUpdatesFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store?.storeId ?? 'unknown'],
                  userId: user?.id ?? 'unknown',
                },
              })),
            ),
          ),
      ),
    ),
  );
  deleteOldMessages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.deleteOldMessages),
        withLatestFrom(this.store.pipe(select(selectUser)),
          this.store.pipe(select(selectSelectedStore))),
        tap(([{messageIds}, {id}, {storeId}]) => {
          void this.collectionUsersMessagesService.batchDeleteMessages(messageIds, id, storeId);
        }),
      ),
    {dispatch: false},
  );


  setInitialVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getVersion),
      switchMap(() =>
        runInInjectionContext(this.envInjector, () => {
          const docRef = doc(this.firestore, VERSION_DOCUMENT_PATH);
          return from(getDoc(docRef)).pipe(
            map(snapshot => {
              const data = snapshot.data();
              return SharedActions.setInitialVersion({
                version: data?.['versionString'] ?? '',
              });
            }),
          );
        }),
      ),
    ),
  );

  checkVersionOnAppLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.checkVersionOnAppLoad),
        switchMap(() =>
          runInInjectionContext(this.envInjector, () => {
            const docRef = doc(this.firestore, VERSION_DOCUMENT_PATH);

            return from(getDoc(docRef)).pipe(
              tap(snapshot => {
                const remoteVersion = snapshot.data()?.['versionString'];

                if (
                  typeof remoteVersion !== 'string'
                  || remoteVersion === ''
                  || remoteVersion === BUILD_VERSION
                ) {
                  return;
                }

                if (getAppLoadRefreshedVersion() === remoteVersion) {
                  return;
                }

                setAppLoadRefreshedVersion(remoteVersion);

                void startHardRefreshWithFollowUp();
              }),
              catchError(() => EMPTY),
            );
          }),
        ),
      ),
    {dispatch: false},
  );

  setChangedVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getVersion),
      withLatestFrom(this.store.pipe(select(selectIsUserLoggedIn))),
      switchMap(([_, isUserLoggedIn]) => {
          if (!isUserLoggedIn) {
            return EMPTY;
          }
          return runInInjectionContext(this.envInjector, () => {
            const docRef = doc(this.firestore, VERSION_DOCUMENT_PATH);
            return docData(docRef).pipe(
              distinctUntilChanged((a, b) => a['versionString'] === b['versionString']),
              pairwise(),
              map(([_, newDoc]) =>
                SharedActions.setChangedVersion({version: newDoc['versionString']}),
              ),
            );
          });
        },
      ),
    ),
  );

  showToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.setChangedVersion),
        delay(TimeEnum.FiveMinutes),
        tap(async ({version}) => {
          await this.sharedToastService.present({
            message: 'Gallix Has Been Updated, Please Refresh',
            position: 'bottom',
            duration: 0,
            colour: 'primary',
            buttons: [
              {
                text: 'Refresh',
                handler: (): void => {
                  void startHardRefreshWithFollowUp();
                },
              },
              {
                text: 'Remind Later',
                handler: (): void => {
                  this.store.dispatch(SharedActions.setChangedVersion({version: version}));
                },
              },
            ],
          });
        }),
      ),
    {dispatch: false},
  );
  private stop$ = new Subject<void>();

  getColleagues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getColleagues),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        if (!user.id) {
          return EMPTY;
        }

        return this.collectionUserAccessColleaguesService.getColleagues(user.id).pipe(
          takeUntil(this.stop$),
          map((users: IColleagues) => SharedActions.getColleaguesSuccess({users})),
          catchError((error) => {
            return of(SharedActions.getColleaguesFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            }));
          }),
        );
      }),
    ),
  );
  killColleaguesService$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.killColleaguesService),
        tap(() => {
          this.stop$.next();
        }),
      ),
    {dispatch: false},
  );

  // '[Auto Order][Add Item] Get Typesense Stock Items'
  getTypesenseStockItems$ = createEffect(() => this.actions$.pipe(
    ofType(AutoOrderActions.getTypesenseStockItems),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(AutoOrderSelectors.selectAddItemLineColours)),
    ),
    mergeMap(([action, store, lineColours]) =>
      from(this.typeSenseService.searchStockItems(
        '',
        1,
        50,
        store,
        {
          code: {value: true, name: 'Code'},
          desc: {value: true, name: 'Description'},
        },
      )).pipe(
        mergeMap((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
          const paginationData: IPaginationData = {
            totalItems: stockItemsRaw.found,
            pageSize: 50,
            currentPage: 1,
            searchedValue: '',
          };
          const mappedStockItems = mapTypeSenseObjectToGeneric<StockItem>(stockItemsRaw);
          const stockItems = mappedStockItems.map((item: StockItem) => ({
            ...item,
            storeId: store.storeId,
            store: store.name,
            lineColourValue: lineColours?.[item.lineColourCode] ? lineColours[item.lineColourCode].value : '',
          }));
          return [
            AutoOrderActions.getTypesenseStockItemsSuccess({
              stockItems,
              paginationData,
            }),
          ];
        }),
        catchError((error: IGlobalError) =>
          of(AutoOrderActions.getTypesenseStockItemsFailure({
            error: {
              ...error,
              action: JSON.stringify(action),
              actionType: action.type,
              originalError: error,
              storeIds: [store.storeId],
            },
          })),
        ),
      ),
    ),
  ));

  // '[Auto Order][Add Item] Get Typesense Stock Items Search Results'
  getSearchResultsForStockItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutoOrderActions.getTypesenseStockItemsSearchResults),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(AutoOrderSelectors.selectAddItemLineColours)),
        this.store.pipe(select(AutoOrderSelectors.selectAddItemFilter)),
      ),
      mergeMap(([action, store, lineColours, filter]) => {
        const {idField, filters} = action;
        return this.typeSenseService
          .searchStockItems(
            idField,
            1,
            50,
            store,
            {
              code: {value: true, name: 'Code'},
              desc: {value: true, name: 'Description'},
            },
            {...filter, ...filters},
          )
          .pipe(
            map((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
              const paginationData: IPaginationData = {
                totalItems: stockItemsRaw.found,
                pageSize: 50,
                currentPage: 1,
                searchedValue: idField,
              };
              const mappedStockItems = mapTypeSenseObjectToGeneric<StockItem>(stockItemsRaw);
              const facetCounts: IFacetCounts = createFacetCountsMap(stockItemsRaw.facet_counts);
              const stockItems = mappedStockItems.map((item: StockItem) => ({
                ...item,
                storeId: store.storeId,
                store: store.name,
                lineColourValue: lineColours?.[item.lineColourCode] ? lineColours[item.lineColourCode].value : '',
              }));
              return [
                AutoOrderActions.getTypesenseStockItemsSearchResultsSuccess({
                  stockItems,
                  paginationData,
                  filters: {...filter, ...filters},
                  facetCounts: facetCounts,
                }),
              ];
            }),
            mergeMap((actions) => from(actions)),
            catchError((error: IGlobalError) =>
              of(AutoOrderActions.getTypesenseStockItemsSearchResultsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                },
              })),
            ),
          );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private collectionOperationalMessagesServiceService: CollectionOperationalMessagesServiceService,
    private collectionSharedSingularDocumentsService: CollectionSharedSingularDocumentsService,
    private collectionSharedSuppliersService: CollectionSharedSuppliersService,
    private collectionSharedDebtorsService: CollectionSharedDebtorsService,
    private collectionUserAccessColleaguesService: CollectionUserAccessColleaguesService,
    private firebaseService: FirebaseService,
    private collectionUsersMessagesService: CollectionUsersMessagesService,
    private storageService: StorageService,
    private firestore: Firestore,
    private sharedToastService: SharedToastService,
    private readonly store: Store,
    private readonly envInjector: EnvironmentInjector,
    private readonly typeSenseService: TypeSenseService,
  ) {
  }

  killSubscription(): void {
    this.stop$.next();
    this.stop$.complete();
  }
}
